import { storage } from './localStorage';
import { decodeJWT } from './jwt';
import { Locale } from 'date-fns';
import { sk } from 'date-fns/locale/sk';
import { cs } from 'date-fns/locale/cs';
import { de } from 'date-fns/locale/de';
import { hu } from 'date-fns/locale/hu';
import { pl } from 'date-fns/locale/pl';
import { enUS } from 'date-fns/locale/en-US';

/**
 * Gets the user's timezone from JWT token stored in localStorage
 * @returns timezone string (e.g., "Europe/Bratislava") or default "Europe/Bratislava"
 */
export const getUserTimezone = (): string => {
  const token = storage.getItem('token');
  if (!token) {
    return 'Europe/Bratislava'; // Default timezone
  }

  const payload = decodeJWT(token);
  return payload?.user?.timezone || 'Europe/Bratislava';
};

/**
 * Gets the user's locale from JWT token stored in localStorage
 * @returns locale string (e.g., "sk-SK") or default "sk-SK"
 */
export const getUserLocale = (): string => {
  const token = storage.getItem('token');
  if (!token) {
    return 'sk-SK'; // Default locale
  }

  const payload = decodeJWT(token);
  // Convert locale format from "sk_SK" to "sk-SK" (JavaScript uses hyphens)
  const locale = payload?.user?.locale || 'sk-SK';
  return locale.replace('_', '-');
};

const localeMap: Record<string, Locale> = {
  'sk': sk, 'sk-SK': sk,
  'cs': cs, 'cs-CZ': cs,
  'de': de, 'de-DE': de, 'de-AT': de,
  'hu': hu, 'hu-HU': hu,
  'pl': pl, 'pl-PL': pl,
  'en': enUS, 'en-US': enUS, 'en-GB': enUS,
};

/**
 * Returns date-fns Locale object based on user's locale from JWT
 */
export const getDateFnsLocale = (): Locale => {
  const userLocale = getUserLocale();
  return localeMap[userLocale] || localeMap[userLocale.split('-')[0]] || sk;
};

/**
 * Converts ISO timestamp string to datetime-local input format (YYYY-MM-DDTHH:mm)
 * Converts UTC timestamps to the user's timezone from JWT token
 * If value is already in datetime-local format (no timezone), returns it unchanged
 * @param isoString - ISO timestamp (e.g., "2025-01-22T14:30:00+01:00" or "2025-01-22 14:30:00" for UTC)
 * @returns datetime-local format string in user's timezone (e.g., "2025-01-22T15:30") or empty string if invalid
 */
export const isoToDatetimeLocal = (isoString: string | null | undefined): string => {
  if (!isoString) return '';

  try {
    // Check if already in datetime-local format (YYYY-MM-DDTHH:mm without seconds or timezone)
    // This happens when user selects a date via calendar and value is stored in form state
    const datetimeLocalMatch = isoString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (datetimeLocalMatch) {
      // Already in datetime-local format, return as-is
      return isoString;
    }

    // Parse the timestamp - if no timezone info, assume UTC
    const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(.*)$/);

    if (!match) return '';

    const [, year, month, day, hours, minutes, seconds = '00', tzPart] = match;

    let date: Date;

    // Check if timezone info is present
    const hasTzInfo = tzPart && (tzPart.includes('+') || tzPart.includes('-') || tzPart.includes('Z'));

    if (hasTzInfo) {
      // Parse as-is - JavaScript will handle the timezone
      date = new Date(isoString);
    } else {
      // No timezone info - assume UTC
      date = new Date(Date.UTC(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds)
      ));
    }

    if (isNaN(date.getTime())) return '';

    const timezone = getUserTimezone();

    // Format date and time in user's timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone
    });

    const parts = formatter.formatToParts(date);
    const dateParts: Record<string, string> = {};
    parts.forEach(part => {
      dateParts[part.type] = part.value;
    });

    // Return in datetime-local format: YYYY-MM-DDTHH:mm
    return `${dateParts.year}-${dateParts.month}-${dateParts.day}T${dateParts.hour}:${dateParts.minute}`;
  } catch (error) {
    return '';
  }
};

/**
 * Gets the timezone offset in minutes for a specific timezone at a specific date
 * Returns positive for timezones ahead of UTC (e.g., +60 for Europe/Bratislava in winter)
 */
const getTimezoneOffsetMinutes = (timezone: string, date: Date): number => {
  // Format the date in UTC and in target timezone, then compare
  const utcFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  const tzFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  const utcStr = utcFormatter.format(date);
  const tzStr = tzFormatter.format(date);

  // Parse formatted strings to get components
  const utcMatch = utcStr.match(/(\d{4})-(\d{2})-(\d{2}),?\s*(\d{2}):(\d{2})/);
  const tzMatch = tzStr.match(/(\d{4})-(\d{2})-(\d{2}),?\s*(\d{2}):(\d{2})/);

  if (!utcMatch || !tzMatch) return 0;

  const utcDate = new Date(Date.UTC(
    parseInt(utcMatch[1]),
    parseInt(utcMatch[2]) - 1,
    parseInt(utcMatch[3]),
    parseInt(utcMatch[4]),
    parseInt(utcMatch[5])
  ));

  const tzDate = new Date(Date.UTC(
    parseInt(tzMatch[1]),
    parseInt(tzMatch[2]) - 1,
    parseInt(tzMatch[3]),
    parseInt(tzMatch[4]),
    parseInt(tzMatch[5])
  ));

  // Difference in minutes (positive = ahead of UTC)
  return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
};

/**
 * Converts timezone offset in minutes to string format (+01:00, -05:30, etc.)
 */
const formatTimezoneOffset = (offsetMinutes: number): string => {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Converts datetime-local format to UTC ISO string (without timezone suffix)
 * Interprets the input as being in user's timezone from JWT token and converts to UTC
 * @param datetimeLocal - datetime-local format (e.g., "2025-01-22T14:30")
 * @returns UTC ISO timestamp without timezone (e.g., "2025-01-22T13:30:00") or null if invalid
 */
export const datetimeLocalToISO = (datetimeLocal: string | null | undefined): string | null => {
  if (!datetimeLocal) return null;

  try {
    // Parse the datetime-local string manually (format: YYYY-MM-DDTHH:mm or YYYY-MM-DDTHH:mm:ss)
    const match = datetimeLocal.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return null;

    const [, year, month, day, hours, minutes, seconds = '00'] = match;

    // Get user's timezone from JWT
    const timezone = getUserTimezone();

    // Create a UTC date first (we'll use this to calculate the offset)
    const utcDate = new Date(Date.UTC(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours),
      parseInt(minutes),
      parseInt(seconds)
    ));

    // Get the timezone offset for user's timezone at this date
    const offsetMinutes = getTimezoneOffsetMinutes(timezone, utcDate);

    // Subtract the offset to convert from user's local time to UTC
    // If user is in +01:00 and selects 22:30, we need to send 21:30 UTC
    const utcTimestamp = utcDate.getTime() - (offsetMinutes * 60 * 1000);
    const resultDate = new Date(utcTimestamp);

    // Format as ISO string without timezone
    const resultYear = resultDate.getUTCFullYear();
    const resultMonth = String(resultDate.getUTCMonth() + 1).padStart(2, '0');
    const resultDay = String(resultDate.getUTCDate()).padStart(2, '0');
    const resultHours = String(resultDate.getUTCHours()).padStart(2, '0');
    const resultMinutes = String(resultDate.getUTCMinutes()).padStart(2, '0');
    const resultSeconds = String(resultDate.getUTCSeconds()).padStart(2, '0');

    return `${resultYear}-${resultMonth}-${resultDay}T${resultHours}:${resultMinutes}:${resultSeconds}+00:00`;
  } catch (error) {
    return null;
  }
};

/**
 * Formats ISO timestamp for display using user's locale and timezone
 * Converts UTC timestamps to the user's timezone from JWT token
 * @param isoString - ISO timestamp (e.g., "2025-01-22T14:30:00+01:00" or "2025-01-22 14:30:00" for UTC)
 * @returns formatted string according to user's locale and timezone (e.g., "22.01.2025 15:30" for sk-SK in Europe/Bratislava) or empty string if invalid
 */
export const formatTimestampForDisplay = (isoString: string | null | undefined): string => {
  if (!isoString) return '';

  try {
    // Parse the timestamp - if no timezone info, assume UTC
    // Formats: "2025-01-22T14:30:00+01:00", "2025-01-22T14:30:00Z", "2025-01-22 14:30:00", "2025-01-22T14:30:00"
    const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(.*)$/);
    if (!match) return '';

    const [, year, month, day, hours, minutes, seconds = '00', tzPart] = match;

    let date: Date;

    // Check if timezone info is present
    const hasTzInfo = tzPart && (tzPart.includes('+') || tzPart.includes('-') || tzPart.includes('Z'));

    if (hasTzInfo) {
      // Parse as-is - JavaScript will handle the timezone
      date = new Date(isoString);
    } else {
      // No timezone info - assume UTC
      date = new Date(Date.UTC(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds)
      ));
    }

    if (isNaN(date.getTime())) return '';

    const locale = getUserLocale();
    const timezone = getUserTimezone();

    // Format date and time in user's timezone
    const formatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone
    });

    // Format returns something like "22. 01. 2025, 15:30" for sk-SK
    // We need to clean it up to "22.01.2025 15:30"
    const parts = formatter.formatToParts(date);
    const dateParts: Record<string, string> = {};
    parts.forEach(part => {
      dateParts[part.type] = part.value;
    });

    const formattedDate = `${dateParts.day}.${dateParts.month}.${dateParts.year}`;
    const formattedTime = `${dateParts.hour}:${dateParts.minute}`;

    return `${formattedDate} ${formattedTime}`;
  } catch (error) {
    return '';
  }
};

/**
 * Converts time string with timezone to HH:mm format for time input
 * @param timeString - Time string (e.g., "14:30:00+02:00" or "14:30:00")
 * @returns time in HH:mm format (e.g., "14:30") or empty string if invalid
 */
export const toTimeInput = (timeString: string | null | undefined): string => {
  if (!timeString) return '';

  try {
    // Match format: HH:mm:ss+TZ or HH:mm:ss or HH:mm
    const match = timeString.match(/^(\d{2}):(\d{2})/);

    if (!match) return '';

    const [, hours, minutes] = match;
    return `${hours}:${minutes}`;
  } catch (error) {
    return '';
  }
};

/**
 * Converts time input (HH:mm) to HH:mm:ss+00:00 format for backend
 * Time is sent as-is with +00:00 suffix (time fields are timezone-independent)
 * @param timeInput - Time in HH:mm format (e.g., "14:30")
 * @returns time in HH:mm:ss+00:00 format (e.g., "14:30:00+00:00") or null if invalid
 */
export const timeInputToBackend = (timeInput: string | null | undefined): string | null => {
  if (!timeInput) return null;

  try {
    // Match format: HH:mm
    const match = timeInput.match(/^(\d{2}):(\d{2})$/);

    if (!match) return null;

    return `${timeInput}:00+00:00`;
  } catch (error) {
    return null;
  }
};

/**
 * Converts a duration in total seconds to "HH:MM" for display/input.
 * Hours accumulate (no 24h wrap): 90000 -> "25:00", 5400 -> "01:30".
 * @param value - total seconds (number or numeric string)
 * @returns "HH:MM" or empty string when value is empty
 */
export const secondsToInterval = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '';

  const total = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(total)) return typeof value === 'string' ? value : '';

  const sign = total < 0 ? '-' : '';
  const abs = Math.floor(Math.abs(total));
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);

  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Converts a duration input ("HH:MM", "HH:MM:SS" or plain seconds) to total seconds.
 * @param input - duration string
 * @returns total seconds, or null when empty/invalid
 */
export const intervalInputToSeconds = (input: string | number | null | undefined): number | null => {
  if (input === null || input === undefined) return null;
  if (typeof input === 'number') return Math.round(input);

  const str = String(input).trim();
  if (str === '') return null;

  const match = str.match(/^(-?)(\d+):([0-5]?\d)(?::([0-5]?\d))?$/);
  if (match) {
    const sign = match[1] === '-' ? -1 : 1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    const seconds = match[4] ? parseInt(match[4], 10) : 0;
    return sign * (hours * 3600 + minutes * 60 + seconds);
  }

  const num = Number(str);
  return isNaN(num) ? null : Math.round(num);
};

/**
 * Live input mask for a duration field while typing.
 * Allows only digits and a single ':'. With no colon yet, the colon is
 * auto-inserted after 2 hour digits; minutes are capped at 2 digits.
 * Hours can grow beyond 2 digits if the user types the ':' themselves.
 * e.g. "1330" -> "13:30", "5" -> "5", "100:00" stays "100:00".
 */
export const maskIntervalInput = (raw: string | null | undefined): string => {
  if (raw == null) return '';
  const s = String(raw).replace(/[^\d:]/g, '');

  const colon = s.indexOf(':');
  if (colon !== -1) {
    const hours = s.slice(0, colon).replace(/\D/g, '');
    const mins = s.slice(colon + 1).replace(/\D/g, '').slice(0, 2);
    return `${hours}:${mins}`;
  }

  if (s.length <= 2) return s;
  return `${s.slice(0, 2)}:${s.slice(2, 4)}`;
};

/**
 * Normalizes a duration string to canonical "HH:MM" (zero-padded).
 * Minutes >= 60 carry over into hours (e.g. "13:99" -> "14:39").
 * A value with no colon is treated as whole hours ("5" -> "05:00").
 */
export const normalizeIntervalDisplay = (value: string | null | undefined): string => {
  if (value == null) return '';
  const s = String(value).trim();
  if (s === '') return '';

  let hours = 0;
  let minutes = 0;

  if (s.includes(':')) {
    const [h, m] = s.split(':');
    hours = parseInt(h.replace(/\D/g, ''), 10) || 0;
    minutes = parseInt((m || '').replace(/\D/g, '').slice(0, 2), 10) || 0;
  } else {
    const digits = s.replace(/\D/g, '');
    if (digits === '') return '';
    hours = parseInt(digits, 10) || 0;
  }

  hours += Math.floor(minutes / 60);
  minutes = minutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Formats date string for display using user's locale
 * @param dateString - Date in YYYY-MM-DD format (e.g., "2025-10-23")
 * @returns formatted string according to user's locale (e.g., "23.10.2025" for sk-SK, "10/23/2025" for en-US) or empty string if invalid
 */
export const formatDateForDisplay = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  try {
    // Match format: YYYY-MM-DD
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';

    const [, year, month, day] = match;
    const date = new Date(`${year}-${month}-${day}T00:00:00`);

    if (isNaN(date.getTime())) return '';

    const locale = getUserLocale();

    const formatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    return formatter.format(date);
  } catch (error) {
    return '';
  }
};

/**
 * Parses ISO/datetime-local string to Date object in user's timezone
 * For use with react-datepicker which works with Date objects
 */
export const parseToDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  try {
    const dtLocal = isoToDatetimeLocal(value);
    if (!dtLocal) return null;
    const match = dtLocal.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!match) return null;
    const [, year, month, day, hours, minutes] = match;
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
  } catch {
    return null;
  }
};

/**
 * Converts Date object to datetime-local string (YYYY-MM-DDTHH:mm)
 */
export const dateToDatetimeLocal = (date: Date | null): string => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Parses YYYY-MM-DD string to Date object
 */
export const parseDateString = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  try {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const [, year, month, day] = match;
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  } catch {
    return null;
  }
};

/**
 * Converts Date object to YYYY-MM-DD string
 */
export const dateToDateString = (date: Date | null): string => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseMonthString = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})/);
  if (!match) return null;
  const date = new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, 1);
  return isNaN(date.getTime()) ? null : date;
};

export const dateToMonthString = (date: Date | null): string => {
  if (!date || isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

export const formatMonthYearForDisplay = (value: string | null | undefined): string => {
  const date = parseMonthString(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(getUserLocale(), { year: 'numeric', month: 'long' }).format(date);
};

/**
 * Parses HH:mm string to Date object (today's date with given time)
 */
export const parseTimeString = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  try {
    const match = value.match(/^(\d{2}):(\d{2})/);
    if (!match) return null;
    const [, hours, minutes] = match;
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return date;
  } catch {
    return null;
  }
};

/**
 * Converts Date object to HH:mm string
 */
export const dateToTimeString = (date: Date | null): string => {
  if (!date) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};